// R21 — THE FOUR THINGS A SPIRIT ON HER SHEET CAN TURN OUT TO BE, AND WHAT A MEMBER IS TOLD
// ABOUT EACH. One law, imported by the server that composes the working and by the reader that
// renders it.
//
// THE SHAPE IS THE ONE THE R16 SWEEP KEEPS FINDING: the server WRITES a kind, the reader BRANCHES
// on it, and the receiving side's default is silence. `resolveRecipeSeals` emits four kinds. The
// spellbook reader branched on ONE of them. The working composer filtered for TWO. So:
//
//   MEASURED, and this is the part that matters: an `unavailable` seal reached NEITHER list in
//   `oilStepCards` — not `pentacles`, not `spirits` — so NO STEP WAS WRITTEN FOR IT AT ALL. A
//   member reads "How to work it" from the first line to the last and never lays a seal her own
//   sheet names. It is not a missing picture; it is a missing instruction, and nothing on the page
//   says a word about it. `composeCauldronSteps` drops it a second time on the candle half.
//
// THE DISTINCTION THIS MODULE EXISTS TO MAKE, and it is R18's, met a third time:
//
//   • `unresolved` — SHE WROTE SOMETHING THE HOUSE DOES NOT RECOGNISE. Her sheet is the authority.
//     It rides through in HER OWN WORDS with no picture and no invented meaning, and that is
//     CORRECT and finished. Nothing is owed.
//   • `unavailable` — SHE NAMED A PENTACLE AND THE HOUSE COULD NOT PRODUCE IT. A picture IS owed
//     and is missing. `resolvePentacle` returns null on a database THROW as well as on a miss, so
//     a transient outage can put a recipe into this state — which means the honest sentence is
//     "we could not bring you the picture", never "she wrote it that way".
//
// Rendering those two identically — which is what shipped — tells a member that a broken lookup is
// her teacher's own handwriting. That is the whole reason they are separate kinds.

// Every kind `resolveRecipeSeals` can emit. Held against the server's own emissions by the fixture,
// so a fifth kind cannot ship into a branch that does not exist.
export const CATALOG_SEAL_KINDS = ["pentacle", "spirit", "unresolved", "unavailable"];

// Does the house owe this row a PICTURE? Only a resolved pentacle carries one; `unavailable` is
// exactly the row that was owed one and did not get it.
export const sealCarriesImage = (kind) => kind === "pentacle";

// EVERY kind gets a step in the working. There is no fifth answer and there is no silent drop —
// that is the defect this round closes, written as a law rather than as four filters that have to
// agree with each other.
export const sealIsWorked = (kind) => CATALOG_SEAL_KINDS.includes(String(kind || ""));

// W17-1(a) — AND THE PROVENANCE CAME OUT OF ALL THREE OF THESE, BY HER ORDER, WHICH SUPERSEDES
// R21's WORDING WHILE LEAVING R21's DISTINCTION EXACTLY WHERE IT WAS.
//
// "(as she wrote it on her sheet)" and "her sheet spells this oil with X" are INGESTION
// SCAFFOLDING: notes about where a row came from, written for whoever was reading the spreadsheet.
// Her ruling is that internal provenance never renders member-facing, and the reason is plain once
// it is said out loud — a member holding a jar and a candle is being told to speak a name, and
// where that name was transcribed from is not a direction she can follow. It also quietly told her
// that the house was unsure.
//
// WHAT DID NOT CHANGE IS THE THING R21 EXISTS FOR: `unresolved` (her own words, nothing owed) and
// `unavailable` (a picture is owed and missing) are still two kinds with two different sentences,
// because rendering them alike is what told a member a broken lookup was her teacher's handwriting.
// Only the provenance phrasing is gone; the distinction it was carried on is intact and still held
// by `scripts/r21-catalog-seals.mjs`.

// The sentence a member is told about this row, beyond its name. Empty for a plain pentacle, whose
// picture and verse say it themselves.
export function catalogSealNote(seal) {
  const kind = String(seal && seal.kind || "");
  if (kind === "spirit") return "Call the name; build the seal yourself on Create Your Own Seals.";
  if (kind === "unresolved") return "Work this one exactly as it is named here.";
  if (kind === "unavailable") return "This seal's picture is not available here right now. Work it exactly as it is named, and check back — nothing else about this working changes.";
  return "";
}

// The Tools line for a row. `unavailable` still does not wear `unresolved`'s wording: a failed
// lookup is never rendered as a finished instruction.
export function catalogSealToolLine(seal) {
  const s = seal || {};
  const name = s.name || s.text || "";
  if (s.kind === "unavailable") return `${name} — its picture is not available here right now.`;
  return name;
}

// The working step for a row that carries no picture. Named here so the oil half and any future
// composer say the same thing, rather than each carrying its own copy of the sentence.
export function catalogSealStep(seal, { over = "the oil" } = {}) {
  const s = seal || {};
  const noun = over === "the oil" ? "oil" : "working";
  if (s.kind === "unresolved") {
    return `This ${noun} is spelled with ${s.text}. Call it over ${over} and tell it plainly what this ${noun} is for.`;
  }
  if (s.kind === "unavailable") {
    return `This ${noun} is spelled with ${s.text}. Its picture is not available here right now — print it from your own copy if you have one, and either way call it over ${over} and tell it plainly what this is for. Do not skip it.`;
  }
  const note = s.herNote ? ` (her note: ${s.herNote})` : "";
  return `Call on ${s.name}${note} over ${over}. Say the name out loud and tell it plainly what this ${noun} is for.${s.meaning ? ` ${s.name} carries: ${s.meaning}` : ""}`;
}
